// Minimal RFC-4180 CSV parsing, for the browser.
//
// This is `server/history/csv.mjs` a second time, on purpose. The engine is
// bundled into the app and the server is not, so a browser module cannot
// import out of `server/` — and pulling the parser down into `src/` instead
// would leave the ingestion scripts importing app code, which is the same
// coupling pointing the other way.
//
// So: two copies, both tiny, both tested, neither importing the other. Keep
// them in step by hand — the tests for each spell out the same four cases
// (quoted fields, CRLF, a BOM, a trailing newline), so a change to one and not
// the other shows up as a test that does not match its twin.
//
// Pure text in, arrays of strings out. Nothing here decides what a column
// MEANS; that is `lib/stores/probsets.js`.

/**
 * Parse CSV text into a header row and the remaining rows, all as raw
 * strings (no type coercion — callers decide what a column means).
 */
export function parseCsv(text) {
  const s = String(text ?? '');
  const body = s.charCodeAt(0) === 0xfeff ? s.slice(1) : s;
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  const n = body.length;
  let i = 0;

  const pushField = () => { row.push(field); field = ''; };
  const pushRow = () => { pushField(); rows.push(row); row = []; };

  while (i < n) {
    const c = body[i];
    if (inQuotes) {
      if (c === '"') {
        if (body[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += c;
      i += 1;
      continue;
    }
    if (c === '"') { inQuotes = true; i += 1; continue; }
    if (c === ',') { pushField(); i += 1; continue; }
    if (c === '\r') {
      if (body[i + 1] === '\n') i += 1;
      pushRow();
      i += 1;
      continue;
    }
    if (c === '\n') { pushRow(); i += 1; continue; }
    field += c;
    i += 1;
  }
  if (field.length > 0 || row.length > 0) pushRow();

  // A trailing newline (or several) leaves one lone empty-field row; drop it.
  while (rows.length && rows[rows.length - 1].length === 1 && rows[rows.length - 1][0] === '') rows.pop();

  const [header, ...rest] = rows;
  return { header: header ?? [], rows: rest };
}

/** Rows keyed by header name, in header order. */
export function rowsToObjects({ header, rows }) {
  return rows.map((r) => Object.fromEntries(header.map((h, i) => [h, r[i] ?? ''])));
}
